import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './BeforeAfter.module.css';

const BARS = 32;

type Variant = 'before' | 'after';

interface TrackProps {
  variant: Variant;
  src: string;
  tag: string;
  icon: string;
  caption: string;
  title: string;
  active: boolean;
  onPlay: () => void;
  onStop: () => void;
}

function Track({ variant, src, tag, icon, caption, title, active, onPlay, onStop }: TrackProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const handleToggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      onPlay();
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => setPlaying(false));
      }
    } else {
      audio.pause();
    }
  }, [onPlay]);

  // Когда стартует другой трек — этот ставим на паузу
  useEffect(() => {
    if (!active) {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
      }
    }
  }, [active]);

  return (
    <article className={`${styles.card} ${variant === 'before' ? styles.cardBefore : styles.cardAfter}`}>
      <header className={styles.top}>
        <span className={styles.tag}>{tag}</span>
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      </header>

      <div className={`${styles.wave} ${styles[variant]} ${playing ? styles.playing : ''}`} aria-hidden="true">
        {Array.from({ length: BARS }).map((_, i) => (
          <span key={i} style={{ animationDelay: `${(i % 16) * 0.07}s` }} />
        ))}
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.play}
          onClick={handleToggle}
          aria-pressed={playing}
          aria-label={playing ? `Пауза: ${tag}` : `Слушать: ${tag}`}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
              <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
            </svg>
          )}
        </button>
        <div className={styles.meta}>
          <p className={styles.caption}>{caption}</p>
          <span className={styles.title}>♪ {title}</span>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlaying={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          onStop();
        }}
      />
    </article>
  );
}

export function BeforeAfter() {
  const [activeId, setActiveId] = useState<number | null>(null);

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>Как это работает</h2>

      <div className={styles.grid}>
        <Track
          variant="before"
          src={`${process.env.PUBLIC_URL}/audio/vesna-plus.mp3`}
          tag="До"
          icon="🎤"
          caption="Оригинал — голос + музыка"
          title="Весна"
          active={activeId === 0}
          onPlay={() => setActiveId(0)}
          onStop={() => setActiveId((id) => (id === 0 ? null : id))}
        />

        <div className={styles.arrow} aria-hidden="true">
          <span>➜</span>
        </div>

        <Track
          variant="after"
          src={`${process.env.PUBLIC_URL}/audio/vesna-minus.mp3`}
          tag="После"
          icon="🎵"
          caption="Чистый минус — голос удалён"
          title="Весна"
          active={activeId === 1}
          onPlay={() => setActiveId(1)}
          onStop={() => setActiveId((id) => (id === 1 ? null : id))}
        />
      </div>
    </section>
  );
}
